export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    SIGNUP: '/auth/signup',
    ME: '/auth/me',
    CHANGE_PASSWORD: '/auth/change-password',
    VERIFY_PASSWORD: '/auth/verify-password',
    CHANGE_USER_PASSWORD: (id: string) => `/auth/users/${id}/password`,
  },
  USERS: {
    BASE: '/users',
    BY_ID: (id: string) => `/users/${id}`,
    APPROVE: (id: string) => `/users/${id}/approve`,
    REJECT: (id: string) => `/users/${id}/reject`,
    STATUS: (id: string) => `/users/${id}/status`,
    INSTITUTION: (id: string) => `/users/${id}/institution`,
    TABS: (id: string) => `/users/${id}/tabs`,
    PROFILE: (id: string) => `/users/${id}/profile`,
    ROLE: (id: string) => `/users/${id}/role`,
    PERMISSIONS: (id: string) => `/users/${id}/permissions`,
    REVOKE_ALL_SESSIONS: (id: string) => `/users/${id}/sessions/revoke-all`,
    /**
     * 서버에서 `/users/:id` 보다 **먼저** 선언돼야 하는 고정 경로들(계약 §3).
     * 프론트는 순서 영향을 받지 않지만, 짝이 맞는지 한눈에 보이도록 모아 둔다.
     */
    LOGIN_LOGS: '/users/login-logs',
    STATS: '/users/stats',
    SESSIONS: '/users/sessions',
    SESSION: (sessionId: string) => `/users/sessions/${sessionId}`,
    USER_LOGIN_LOGS: (id: string) => `/users/${id}/login-logs`,
  },
  KIOSKS: {
    BASE: '/kiosks',
    BY_ID: (id: string) => `/kiosks/${id}`,
    CONNECT: (id: string) => `/kiosks/${id}/connect`,
  },
  SESSIONS: {
    ACTIVE: '/sessions/active',
    END: (id: string) => `/sessions/${id}/end`,
  },
  ANALYTICS: {
    EVENTS: '/analytics/events',
    SESSIONS: '/analytics/sessions',
    SESSION: (id: string) => `/analytics/sessions/${id}`,
    DASHBOARD: '/analytics/dashboard',
    KIOSK_DASHBOARD: (id: string) => `/analytics/kiosks/${id}/dashboard`,
    DAU: '/analytics/dau',
    MAU: '/analytics/mau',
    USER_STATS: '/analytics/users/stats',
    API_USAGE: '/analytics/api-usage',
  },
} as const;

export const QUERY_KEYS = {
  KIOSKS: 'kiosks',
  KIOSK: 'kiosk',
  SESSIONS: 'sessions',
  USERS: 'users',
  ANALYTICS: 'analytics',
  /** `GET /auth/me` 캐시. 새로고침 직후 프로필 복원에 쓴다 */
  AUTH_ME: 'auth-me',
} as const;

export const REFRESH_INTERVAL = 30000; // 30 seconds

/**
 * VNC = Value Navigation Center
 * 기업 가치 상승을 이끄는 통합 관리 센터.
 * 루시케어의 통합 관리 서비스이며, 화면/문서에 노출되는 브랜드 문구는 모두 여기를 참조한다.
 */
export const BRAND = {
  NAME: 'Lucycare VNC',
  SERVICE: '루시케어 통합 관리 서비스',
  ACRONYM: 'VNC',
  EXPANSION: 'Value Navigation Center',
  MEANING: '기업 가치 상승을 이끄는 통합 관리 센터',
  REASON: '단순 기술 도구를 넘어 조직 업무 효율 증대를 돕는 솔루션임을 어필.',
  DESCRIPTION: '단순한 기술 도구를 넘어, 조직의 업무 효율 증대를 돕는 루시케어 통합 관리 서비스입니다.',
  PILLARS: ['원격 제어', '모니터링', '자원 관리'] as const,
} as const;

/**
 * 화면에 노출하는 앱 버전.
 *
 * `package.json` 의 version 은 배포와 함께 올리지 않는 값이라(0.0.0 고정)
 * 사용자에게 보여줄 번호는 여기서 관리한다.
 */
export const APP_VERSION = '1.0.0';

/** 지금 붙어 있는 백엔드 주소. `api.ts` 의 baseURL 과 같은 규칙을 쓴다 */
export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

/**
 * 기관 셀렉트의 "전체 기관" 값.
 *
 * 빈 문자열을 쓰지 않는다 — 기관 셀렉트에서 빈 값은 "아직 안 골랐다" 라는 뜻이고,
 * 그때는 기본값(전체 또는 첫 기관)을 채워 주게 되어 있다. 전체를 고른 것과 못
 * 고른 것이 같은 값이면 URL·기억으로 들어온 전체 보기가 곧바로 기본값으로 튕긴다.
 * 서버 경로에 쓰는 `institutionApi.service` 의 `ALL_INSTITUTIONS`('all')와는 다른
 * 값이다 — 이쪽은 화면 셀렉트·URL 에만 쓰고 서버로 보내지 않는다.
 */
export const ALL_INSTITUTIONS_OPTION = '__all__';
